import { Types } from '@cornerstonejs/core';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import { annotation } from '@cornerstonejs/tools';
import {
  mapAnnotationToMeasurement,
  addSimpsonResult,
} from '@/state/slices/measurementSession';
import { calculateSimpsonVolume } from '@/utils/simpsonMethod';
import { normalizeUnit } from '@/utils/measurementToolMapping';
import type { AppDispatch } from '@/state/store';
import { getBaseAnnotationStyle } from '../../utils/annotationStyle';

/**
 * Encephalon's segmentation payload emits Simpson's-method disks as pairs of
 * endpoints on opposite cavity walls, interleaved one disk at a time:
 * `[wall1₀, wall2₀, wall1₁, wall2₁, …]`, optionally followed by an
 * annulus-base pair whose endpoints span the cavity along the long axis
 * (Δ along the long axis ≈ full cavity span, not a short cross-chord).
 *
 * Drawn naively as a polyline this zigzags across the cavity. Reorder into
 * a closed outline by tracing one wall apex→base, then the other wall
 * base→apex, dropping the annulus pair.
 */
function reorderSimpsonOutline(raw: Types.Point3[]): Types.Point3[] {
  if (raw.length < 4 || raw.length % 2 !== 0) return raw;

  const xs = raw.map((p) => p[0]);
  const ys = raw.map((p) => p[1]);
  const xSpread = Math.max(...xs) - Math.min(...xs);
  const ySpread = Math.max(...ys) - Math.min(...ys);
  const longAxisIsX = xSpread >= ySpread;
  const annulusThresh = (longAxisIsX ? xSpread : ySpread) * 0.5;

  const wall1: Types.Point3[] = [];
  const wall2: Types.Point3[] = [];
  for (let i = 0; i + 1 < raw.length; i += 2) {
    const a = raw[i];
    const b = raw[i + 1];
    const dLong = longAxisIsX ? Math.abs(a[0] - b[0]) : Math.abs(a[1] - b[1]);
    if (dLong <= annulusThresh) {
      wall1.push(a);
      wall2.push(b);
    }
  }
  return [...wall1, ...wall2.reverse()];
}

const VOLUME_UNITS = new Set(['ml', 'mL', 'ML']);

interface HydrateVolumeArgs {
  uid: string;
  /** Display label — caller prefixes `AI: ` for AI measurements. */
  label: string;
  isAI: boolean;
  canonicalMeasurement: string;
  /** Saved value (mL for volumes, cm² for areas); null falls back to the recompute. */
  value: number | null;
  unit: string;
  points: number[][];
  dicomUuid: string;
  frameOfReferenceUID: string;
  /** Matches the viewport's display filter — video:... or web:... for stills. */
  refImageId: string;
  /** Matches the tool's render targetId — videoId:... or imageId:... */
  statsKey: string;
  /** Encephalon interleaved disk pairs need reordering; human traces are already ordered. */
  reorderOutline: boolean;
  /** em2 contour_* polygon: plain closed area, no Simpson volume/disks. */
  isArea?: boolean;
  viewport: CornerstoneTypes.IStackViewport | Types.IVideoViewport;
  camera: ReturnType<
    (CornerstoneTypes.IStackViewport | Types.IVideoViewport)['getCamera']
  >;
  canEdit: boolean;
  pixelSpacing?: number;
  dispatch: AppDispatch;
  /** 1-based frame the contour lives on. */
  frameNumber: number;
  /** Measurement abbreviation (e.g. "EDV") for the hover chip. */
  abbreviation?: string;
  /** Whether this contour is the currently-selected annotation — so hydration
   *  re-applies the selected (not base) style, matching the linear paths. */
  isSelected?: boolean;
}

/**
 * Hydrates one polygon measurement (AI or human) into Cornerstone state.
 *
 * Volumes pre-compute Simpson (long-axis + disks) and polygon area so the
 * draggable text box surfaces Area + Vol + L immediately, and dispatch a
 * Simpson overlay payload so useSimpsonOverlay draws the disks. Areas
 * (`isArea`) skip all of that and surface Area alone.
 *
 * Returns true when an annotation was added so the caller can trigger a
 * single viewport render at the end of the hydration pass.
 */
export function hydrateVolumeAnnotation({
  uid,
  label,
  isAI,
  canonicalMeasurement,
  value,
  unit,
  points,
  dicomUuid,
  frameOfReferenceUID,
  refImageId,
  statsKey,
  reorderOutline,
  isArea = false,
  viewport,
  camera,
  canEdit,
  pixelSpacing,
  dispatch,
  frameNumber,
  abbreviation,
  isSelected,
}: HydrateVolumeArgs): boolean {
  try {
    const rawPoints = points.map((p) => [...p] as Types.Point3);
    const outlinePoints = reorderOutline
      ? reorderSimpsonOutline(rawPoints)
      : rawPoints;
    // Geometrically close the polyline by repeating the first point at
    // the end. updateContourPolyline auto-detects `closed` by comparing
    // first==last when no explicit flag is passed (Cornerstone source);
    // without the repeat any edit/textbox-move path flips contour.closed
    // to false and the polygon renders with a gap at the apex seam.
    const closedPoints =
      outlinePoints.length > 0
        ? [...outlinePoints, [...outlinePoints[0]] as Types.Point3]
        : outlinePoints;

    // Centroid of the polyline for textBox placement.
    const n = closedPoints.length;
    const sumX = closedPoints.reduce((s, p) => s + p[0], 0);
    const sumY = closedPoints.reduce((s, p) => s + p[1], 0);
    const centroid: Types.Point3 = [sumX / n, sumY / n, 0];
    // Signature for skipping no-op recomputes in useMeasurementCapture.
    // ANNOTATION_MODIFIED fires on textbox click/drag too; recomputing
    // there would overwrite the saved mL Vol with the FE's
    // pixel-space calculation.
    const polylineSig = `${n}|${sumX.toFixed(2)}|${sumY.toFixed(2)}`;

    // Pre-compute Simpson + polygon area so the textbox surfaces Area
    // + Vol + L immediately — without this the user sees only Vol
    // until they edit the contour and Cornerstone recomputes.
    // Use the UNclosed outline so disks match the live-draw path exactly.
    const isVolume = !isArea && (isAI || VOLUME_UNITS.has(unit));
    const contour2D = outlinePoints.map((p) => ({ x: p[0], y: p[1] }));
    let simpsonResult: ReturnType<typeof calculateSimpsonVolume> | null = null;
    if (isVolume && contour2D.length >= 10) {
      try {
        simpsonResult = calculateSimpsonVolume(contour2D, {
          numberOfDisks: 20,
          pixelSpacing: pixelSpacing ?? 1,
        });
      } catch {
        simpsonResult = null;
      }
    }

    // Shoelace polygon area (px²) → mm² via pixelSpacing². Only seed
    // when we have real DICOM spacing; otherwise cm² would be wrong.
    let areaMm2: number | null = null;
    if (pixelSpacing && contour2D.length >= 3) {
      let acc = 0;
      for (let i = 0; i < contour2D.length; i += 1) {
        const a = contour2D[i];
        const b = contour2D[(i + 1) % contour2D.length];
        acc += a.x * b.y - b.x * a.y;
      }
      areaMm2 = (Math.abs(acc) / 2) * pixelSpacing * pixelSpacing;
    }

    // Encephalon's value wins — the estimate above needs spacing videos lack.
    if (isArea && value !== null) {
      areaMm2 = normalizeUnit(unit) === 'cm2' ? value * 100 : value;
    }

    const seededCachedStats: Record<string, { area?: number }> = {};
    if (areaMm2 !== null) {
      seededCachedStats[statsKey] = { area: areaMm2 };
    }

    const volumeForStats = value ?? simpsonResult?.volume ?? null;

    annotation.state.addAnnotation(
      {
        annotationUID: uid,
        data: {
          handles: {
            // No vertex handles: a grab on one starts Cornerstone's open-end draw, not a reshape.
            points: [],
            activeHandleIndex: null,
            textBox: {
              hasMoved: false,
              worldPosition: centroid,
              worldBoundingBox: {
                topLeft: centroid,
                topRight: centroid,
                bottomLeft: centroid,
                bottomRight: centroid,
              },
            },
          },
          // PlanarFreehandROI renders from data.contour.polyline, not
          // data.handles.points. Without this the annotation is added to
          // state but draws nothing on the viewer.
          contour: {
            polyline: closedPoints,
            closed: true,
          },
          label: '',
          measurementLabel: label,
          abbreviation,
          isAI,
          // Volumes keep their no-op guard on simpsonStats; areas have none to hang it on.
          ...(isArea ? { polylineSig } : {}),
          // customFreehandROIGetTextLines reads simpsonStats to render
          // Vol + L. Volume stays as the saved mL value; long axis
          // is derived from the contour in DICOM pixel space.
          // polylineSig short-circuits no-op recomputes on textbox
          // click; useMeasurementCapture compares this to skip.
          ...(isVolume && volumeForStats !== null
            ? {
                simpsonStats: {
                  volume: volumeForStats,
                  volumeUnit: unit,
                  ...(simpsonResult && pixelSpacing
                    ? {
                        longAxisLength: simpsonResult.longAxisLength,
                        longAxisUnit: simpsonResult.longAxisUnit,
                      }
                    : {}),
                  polylineSig,
                },
              }
            : {}),
          // Pre-seed area so the textbox shows Area on first render.
          // PlanarFreehandROI's _calculateStatsIfActive only overwrites
          // this when the annotation is invalidated (after edit).
          cachedStats: seededCachedStats,
        },
        highlighted: false,
        autoGenerated: false,
        invalidated: false,
        isLocked: !canEdit,
        isVisible: true,
        metadata: {
          toolName: 'PlanarFreehandROI',
          FrameOfReferenceUID: frameOfReferenceUID,
          referencedImageId: refImageId,
          viewPlaneNormal: [0, 0, 1] as Types.Point3,
          viewUp: camera.viewUp ?? ([0, -1, 0] as Types.Point3),
          cameraFocalPoint: camera.focalPoint ?? centroid,
          cameraPosition: camera.position ?? ([0, 0, 0] as Types.Point3),
        },
      },
      viewport.element
    );

    annotation.locking.setAnnotationLocked(uid, !canEdit);

    // Source-tinted contour: green for AI, blue for human — same system as
    // linear. Selection recolors the contour (no endpoint handles; freehand is
    // edited with the tool). Honor an existing selection so a cross-dicom
    // re-hydration keeps the selected style, matching the linear paths.
    annotation.config.style.setAnnotationStyles(
      uid,
      getBaseAnnotationStyle({ data: { isAI } }, { isSelected: !!isSelected })
    );

    dispatch(
      mapAnnotationToMeasurement({
        annotationUID: uid,
        canonicalMeasurement,
      })
    );

    // Push Simpson geometry into Redux so useSimpsonOverlay draws the
    // disks / long-axis / apex+base markers on top of the contour —
    // matches the look of manually-traced ESV/EDV.
    //
    // volume: the displayed value, not simpsonResult.volume — the recompute is
    // PIXEL-scale on video frames (no DICOM pixel spacing). This result only
    // feeds the overlay and the text box; nothing folds it into the report.
    if (simpsonResult) {
      dispatch(
        addSimpsonResult({
          ...simpsonResult,
          ...(value !== null && value !== undefined
            ? { volume: value, volumeUnit: unit }
            : {}),
          annotationUID: uid,
          frameIndex: frameNumber - 1,
          dicomUuid,
          reportMeasurementClassUuid: canonicalMeasurement,
          measurementLabel: label,
        })
      );
    }

    return true;
  } catch {
    // Viewport not fully ready
    return false;
  }
}
